import {frontlineOptionName} from '../../../../contracts/product-display-names-v1.ts';
import {mfMoney} from '../../../../contracts/mf-money.ts';
import {displayMinorAmount} from '../../presentation/display-money-r31.ts';
import {useEffect,useMemo,useRef,useState,type ReactNode} from 'react';
import {projectBentoFamilyViewsV1,type BentoFamilyViewV1} from '../../../../contracts/bento-family-view-v1.ts';
import type {SyncedCombo,SyncedComboPool,SyncedOptionSet} from '../../runtime/published-config-projection.ts';
import type {MfkOrderLineOptionSelectionsV1,MfkOrderLinePairingV1} from '../../../../contracts/order-line-composition-v1.ts';
import type {MfkMealLinkV1} from '../../../../contracts/meal-link-v1.ts';
import {resolveProductVariant} from '../../../../contracts/product-variant-v1.ts';
import {drinkSupplementConfigurationView} from './drink-supplement-choices.ts';
import './ordering-center-workspaces.css';
import './ordering-config-r19.css';
import {PublishedOptionTiles} from './PublishedOptionTiles.tsx';
import './ordering-modal-r26.css';
import './ordering-modal-r28.css';
import type {CartLineViewModel,ServiceMode} from './ordering-workspace-model.ts';

export interface WorkspaceProduct{
  readonly id:string;
  readonly category:string;
  readonly name:string;
  readonly productCode?:string;
  readonly shortName?:string;
  readonly priceMinor:number;
  readonly priceLabel:string;
  readonly imageUrl?:string;
  readonly optionSets?:readonly SyncedOptionSet[];
}
export type WorkspaceBentoFamily=Omit<BentoFamilyViewV1,'members'|'defaultProductId'>&{
  readonly defaultProductId:string;
  readonly members:readonly {readonly productId:string;readonly label:string;readonly available:boolean}[];
};
/** Display metadata only; every member remains its original orderable SKU. */
export function projectWorkspaceBentoFamilies(value:unknown,products:readonly {readonly id:string;readonly priceReady?:boolean;readonly sellable?:boolean}[],categoryIds:readonly string[]):readonly WorkspaceBentoFamily[]{
  const byId=new Map(products.map(product=>[product.id,product]));
  return projectBentoFamilyViewsV1(value,{productIds:new Set(byId.keys()),categoryIds:new Set(categoryIds)})
    .filter(family=>family.members.every(member=>byId.get(member.productId)?.priceReady!==false))
    .map(family=>({...family,defaultProductId:family.defaultProductId??family.members[0].productId,members:family.members.map(member=>({...member,available:byId.get(member.productId)?.sellable!==false}))}));
}
export function projectWorkspaceBentoCards<T extends {readonly id:string;readonly name:string;readonly categoryId:string}>(products:readonly T[],families:readonly WorkspaceBentoFamily[]):readonly (T&{readonly bentoFamily?:WorkspaceBentoFamily})[]{
  const assigned=new Set(families.flatMap(family=>family.members.map(member=>member.productId)));
  return [...products.filter(product=>!assigned.has(product.id)),...families.flatMap(family=>{
    const product=products.find(product=>product.id===family.defaultProductId);
    return product?[{...product,name:family.name,categoryId:family.categoryId,bentoFamily:family}]:[];
  })];
}
export function BentoFamilyWorkspace({family,initialProductId,products,renderProduct}:{family:WorkspaceBentoFamily;initialProductId?:string;products:readonly WorkspaceProduct[];renderProduct:(productId:string,basePicker:ReactNode)=>ReactNode}){
  const [productId,setProductId]=useState(()=>family.members.some(member=>member.productId===initialProductId)?initialProductId!:family.defaultProductId);
  const basePicker=<section className="cfg-block cfg19-card bento-base-area" aria-label={family.name+' 飯底'}>
    <header><b>飯底</b></header><div className="cfg19-chips">{family.members.map(member=><button type="button" key={member.productId} disabled={!member.available} className={member.productId===productId?'on':''} aria-pressed={member.productId===productId} onClick={()=>setProductId(member.productId)}><b>{member.label}{products.find(product=>product.id===member.productId)?.productCode?"（"+products.find(product=>product.id===member.productId)!.productCode+"）":""}</b><small>{products.find(product=>product.id===member.productId)?.priceLabel??'停售'}</small></button>)}</div>
  </section>;
  return <section data-bento-family={family.familyId} style={{display:'flex',flexDirection:'column',flex:1,minHeight:0}}>
    {renderProduct(productId,basePicker)}
  </section>;
}
export interface WorkspaceCartLine{
  readonly id:string;
  readonly productId:string;
  readonly name:string;
  readonly qty:number;
  readonly unitMinor:number;
  readonly detail?:string;
  readonly optionSelections?:MfkOrderLineOptionSelectionsV1;
  readonly freeNote?:string;
  readonly pairing?:MfkOrderLinePairingV1;
  readonly meal?:MfkMealLinkV1;
}
export type OrderingPanelState=
  |{readonly type:'product';readonly productId:string;readonly lineId?:string}
  |{readonly type:'required'}
  |{readonly type:'drink-config';readonly choiceId:string;readonly qty:number;readonly targetLineId?:string;readonly returnTo?:'required'|'riceball-pair'}
  |{readonly type:'riceball-pair'}
  |{readonly type:'pending-order';readonly orderId:string}
  |{readonly type:'organize'}
  |{readonly type:'combo';readonly comboId?:string;readonly editLineId?:string}
  |{readonly type:'hold'}
  |{readonly type:'holds'}
  |null;

const money=displayMinorAmount;

export function quickConfigurationForProduct(product:WorkspaceProduct){
  const sets=product.optionSets??[];
  // Owner A3b: Quick mode may admit Required groups into Cart unresolved.
  // forceShow-only groups still use the full editor unless separately decided.
  if(sets.some(set=>set.forceShow&&!set.required&&set.min<=0))return Object.freeze({eligible:false,detail:'',deltaMinor:0});
  // 便當合一 P2：變體組（例如 飯底）有 Admin 預設就當已揀，唔使補選。
  const variantDefaulted=(set:SyncedOptionSet)=>Boolean(set.variant?.enabled)&&set.options.filter(option=>option.defaultSelected).length===1;
  const chosen=sets.map(set=>({
    set,
    options:(set.required||set.min>0)&&!variantDefaulted(set)?[]:set.options.filter(option=>option.defaultSelected),
  }));
  const invalid=chosen.some(({set,options})=>(set.required||set.min>0)&&!variantDefaulted(set)?false:options.length<set.min||options.length>set.max);
  if(invalid)return Object.freeze({eligible:false,detail:'',deltaMinor:0});
  const deltaMinor=chosen.reduce((sum,row)=>sum+row.options.reduce((value,option)=>value+option.priceAdjustmentMinor,0),0);
  const detail=chosen.flatMap(({set,options})=>{
    const names=options.map(option=>option.name);
    return names.length?[set.name+'：'+names.join('、')]:[];
  }).join(' · ');
  return Object.freeze({eligible:true,detail,deltaMinor});
}

import {DRINK_SUPPLEMENT_PRODUCT_PREFIX,isDrinkSupplementProductId,projectDrinkSupplementChoices,type WorkspaceDrinkSupplementChoice} from './drink-supplement-choices.ts';
export {DRINK_SUPPLEMENT_PRODUCT_PREFIX,isDrinkSupplementProductId,projectDrinkSupplementChoices,type WorkspaceDrinkSupplementChoice} from './drink-supplement-choices.ts';

export interface WorkspaceRequiredTask{
  readonly id:string;
  readonly lineId:string;
  readonly lineName:string;
  readonly groupId:string;
  readonly groupName:string;
  readonly selection:'SINGLE'|'MULTI';
  readonly min:number;
  readonly max:number;
  readonly missingCount:number;
  readonly selectedOptionIds:readonly string[];
  readonly options:readonly {id:string;name:string;shortName?:string;priceAdjustmentMinor:number}[];
}

function explicitConfigurationFromDetail(product:WorkspaceProduct,detail?:string){
  const sets=product.optionSets??[];
  const selected:Record<string,string[]>={};
  const noteParts:string[]=[];
  const parts=String(detail??'').split(' · ').map(part=>part.trim()).filter(Boolean);
  for(const part of parts){
    const set=sets.find(row=>part.startsWith(row.name+'：'));
    if(!set){noteParts.push(part);continue;}
    const labels=part.slice(set.name.length+1).split('、').map(label=>label.trim()).filter(Boolean);
    const ids=set.options.filter(option=>labels.includes(option.name)).map(option=>option.id);
    if(labels.length&&ids.length===labels.length)selected[set.id]=ids;
    else noteParts.push(part);
  }
  return {selected,note:noteParts.join(' · ')};
}

export function requiredTasksForCart(lines:readonly WorkspaceCartLine[],products:readonly WorkspaceProduct[]):WorkspaceRequiredTask[]{
  const byProduct=new Map(products.map(product=>[product.id,product] as const));
  const tasks:WorkspaceRequiredTask[]=[];
  for(const line of lines){
    const product=byProduct.get(line.productId);
    if(!product)continue;
    const current=line.optionSelections??explicitConfigurationFromDetail(product,line.detail).selected;
    for(const set of product.optionSets??[]){
      const min=Math.max(set.required?1:0,set.min);
      if(min<=0)continue;
      const max=Math.max(min,set.max||min);
      const ids=(current[set.id]??[]).filter(id=>set.options.some(option=>option.id===id));
      if(ids.length>=min&&ids.length<=max)continue;
      tasks.push({
        id:line.id+'::'+set.id,
        lineId:line.id,
        lineName:line.name,
        groupId:set.id,
        groupName:set.name,
        selection:set.selection,
        min,
        max,
        missingCount:Math.max(0,min-ids.length),
        selectedOptionIds:ids,
        options:set.options.map(option=>({id:option.id,name:option.name,...(option.shortName?{shortName:option.shortName}:{}),priceAdjustmentMinor:option.priceAdjustmentMinor})),
      });
    }
  }
  return tasks;
}

export function applyRequiredSelectionToCart<T extends WorkspaceCartLine>(
  lines:readonly T[],
  products:readonly WorkspaceProduct[],
  lineId:string,
  groupId:string,
  optionIds:readonly string[],
):T[]{
  const byProduct=new Map(products.map(product=>[product.id,product] as const));
  return lines.map(line=>{
    if(line.id!==lineId)return line;
    const product=byProduct.get(line.productId);
    if(!product)throw new Error('REQUIRED_FAST_LANE_PRODUCT_NOT_FOUND');
    const set=(product.optionSets??[]).find(row=>row.id===groupId);
    if(!set)throw new Error('REQUIRED_FAST_LANE_GROUP_NOT_FOUND');
    const allowed=new Set(set.options.map(option=>option.id));
    const unique=[...new Set(optionIds)].filter(id=>allowed.has(id));
    const min=Math.max(set.required?1:0,set.min);
    const max=Math.max(min,set.max||min||1);
    const normalized=set.selection==='SINGLE'?unique.slice(0,1):unique.slice(0,max);
    if(normalized.length<min||normalized.length>max)throw new Error('REQUIRED_FAST_LANE_SELECTION_INVALID');

    const configuration=explicitConfigurationFromDetail(product,line.detail);
    const selected:Record<string,string[]>={...Object.fromEntries(
      Object.entries(line.optionSelections??configuration.selected).map(([id,ids])=>[id,[...ids]])
    ),[groupId]:normalized};
    const freeNote=line.freeNote??configuration.note;
    let deltaMinor=0;
    const detailParts:string[]=[];
    for(const optionSet of product.optionSets??[]){
      const ids=selected[optionSet.id]??[];
      const idSet=new Set(ids);
      const options=optionSet.options.filter(option=>idSet.has(option.id));
      if(options.length){
        detailParts.push(optionSet.name+'：'+options.map(option=>option.name).join('、'));
        deltaMinor+=options.reduce((sum,option)=>sum+option.priceAdjustmentMinor,0);
      }
    }
    if(freeNote)detailParts.push(freeNote);
    return {
      ...line,
      unitMinor:product.priceMinor+deltaMinor,
      detail:detailParts.join(' · ')||undefined,
      optionSelections:Object.freeze(Object.fromEntries(
        Object.entries(selected).flatMap(([id,ids])=>ids.length?[[id,Object.freeze([...ids])] as const]:[])
      )),
      freeNote,
    } as T;
  });
}

export function productEditorInitialFromDetail(product:WorkspaceProduct,detail?:string){
  const sets=product.optionSets??[];
  const selected:Record<string,string[]>=Object.fromEntries(
    sets.map(set=>[set.id,set.options.filter(option=>option.defaultSelected).map(option=>option.id)]),
  );
  const noteParts:string[]=[];
  const parts=String(detail??'').split(' · ').map(part=>part.trim()).filter(Boolean);
  for(const part of parts){
    const set=sets.find(row=>part.startsWith(row.name+'：'));
    if(!set){noteParts.push(part);continue;}
    const labels=part.slice(set.name.length+1).split('、').map(label=>label.trim()).filter(Boolean);
    const ids=set.options.filter(option=>labels.includes(option.name)).map(option=>option.id);
    if(labels.length&&ids.length===labels.length)selected[set.id]=ids;
    else noteParts.push(part);
  }
  return Object.freeze({selected:Object.freeze(selected),note:noteParts.join(' · ')});
}

/** Optional single selections share one widget, independent of names and option counts. */
export function isTileOptionSet(set:SyncedOptionSet){
  return set.selection==='SINGLE'&&!set.variant?.enabled&&!set.required&&set.min===0;
}

export function toggleProductOptionSelection(current:Readonly<Record<string,readonly string[]>>,set:SyncedOptionSet,optionId:string):Record<string,string[]>{
  const existing=current[set.id]??[],on=existing.includes(optionId);
  const next=set.selection==='SINGLE'?(on?[]:[optionId]):(on?existing.filter(id=>id!==optionId):[...existing,optionId]).slice(0,Math.max(1,set.max||existing.length+1));
  return {...Object.fromEntries(Object.entries(current).map(([id,ids])=>[id,[...ids]])),[set.id]:next};
}

/** View state only; saved orders keep their existing optionSelections/meal fields. */
export interface WorkspaceChoiceConfiguration{
  readonly choiceId:string;readonly optionSelections:MfkOrderLineOptionSelectionsV1;
  readonly freeNote:string;readonly detail:string;readonly delta:number;
}
export interface WorkspaceComboSelection{
  readonly poolId:string;readonly groupId:string;readonly subPoolId:string;readonly choiceId:string;
  readonly configuration?:WorkspaceChoiceConfiguration;
}

/** Recompute from published IDs. Changing identity discards the previous choice's IDs and note. */
export function workspaceChoiceConfiguration(choice:WorkspaceDrinkSupplementChoice,products:readonly WorkspaceProduct[],previous?:Pick<WorkspaceChoiceConfiguration,'choiceId'|'optionSelections'|'freeNote'>){
  const view=drinkSupplementConfigurationView(choice,products),sets=view?.optionSets??[];
  const same=previous?.choiceId===choice.id;
  const selected=same?previous.optionSelections:Object.fromEntries(sets.map(set=>[set.id,set.options.filter(option=>option.active&&option.defaultSelected).map(option=>option.id)]));
  const freeNote=same?previous.freeNote:'';
  const valid=choice.enabled&&(choice.kind!=='PRODUCT'||Boolean(view))&&(!choice.requiresConfiguration||Boolean(view))&&
    Object.keys(selected).every(id=>sets.some(set=>set.id===id))&&sets.every(set=>{
      const ids=selected[set.id]??[],minimum=Math.max(set.min,set.required?1:0);
      return ids.length>=minimum&&ids.length<=set.max&&(set.selection!=='SINGLE'||ids.length<=1)&&new Set(ids).size===ids.length&&ids.every(id=>set.options.some(option=>option.id===id&&option.active));
    });
  const options=sets.flatMap(set=>set.options.filter(option=>(selected[set.id]??[]).includes(option.id)&&option.active));
  const detail=[...sets.flatMap(set=>{const labels=set.options.filter(option=>(selected[set.id]??[]).includes(option.id)&&option.active).map(option=>option.name);return labels.length?[set.name+'：'+labels.join('、')]:[];}),freeNote.trim()].filter(Boolean).join(' · ');
  return {choiceId:choice.id,optionSelections:Object.freeze(Object.fromEntries(Object.entries(selected).map(([id,ids])=>[id,Object.freeze([...ids])]))),freeNote,detail,delta:options.reduce((sum,option)=>sum+option.priceAdjustmentMinor,0),valid};
}

/** Full published pool/group identity is required even when groups reuse local IDs. */
export function resolveWorkspaceComboChoice(pools:readonly SyncedComboPool[],poolId:string,groupId:string,identity:string){
  const pool=pools.find(row=>row.id===poolId),group=pool?.groups.find(row=>row.id===groupId);
  if(!pool||!group)return undefined;
  for(const subPool of group.subPools){const choice=subPool.choices.find(row=>[pool.id,group.id,subPool.id,row.id].join('::')===identity);if(subPool.active&&choice?.active)return {pool,group,subPool,choice};}
  return undefined;
}

export function PublishedChoiceFields({choice,products,value,onChange,direct=false}:{choice:WorkspaceDrinkSupplementChoice;products:readonly WorkspaceProduct[];value:WorkspaceChoiceConfiguration;onChange:(value:WorkspaceChoiceConfiguration)=>void;direct?:boolean}){
  const view=drinkSupplementConfigurationView(choice,products),configured=workspaceChoiceConfiguration(choice,products,value);
  const summary=[...(view?.optionSets??[]).flatMap(set=>set.options.filter(option=>configured.optionSelections[set.id]?.includes(option.id)).map(frontlineOptionName)),configured.freeNote].filter(Boolean).join('・');
  return <section className="cfg-block cfg19-card" data-inline-choice={choice.id} aria-label={choice.label+' 選項'}>
    <header><b>{choice.label}</b>{summary?<small>{summary}</small>:null}</header>
    {direct?<div className="direct-drink-options">{(view?.optionSets??[]).map(set=><fieldset key={set.id} aria-label={set.name}><legend>{set.name.replace(/^套餐普通飲品/,'')}</legend><div>{set.options.map(option=><button key={option.id} type="button" disabled={!option.active||set.selection==='MULTI'&&!value.optionSelections[set.id]?.includes(option.id)&&(value.optionSelections[set.id]?.length??0)>=set.max} aria-pressed={Boolean(value.optionSelections[set.id]?.includes(option.id))} onClick={()=>onChange(workspaceChoiceConfiguration(choice,products,{...value,optionSelections:toggleProductOptionSelection(value.optionSelections,set,option.id)}))}>{frontlineOptionName(option)}{option.priceAdjustmentMinor?<small>{money(option.priceAdjustmentMinor)}</small>:null}</button>)}</div></fieldset>)}</div>:<PublishedOptionTiles sets={view?.optionSets??[]} selected={value.optionSelections} onSelect={(set,id)=>onChange(workspaceChoiceConfiguration(choice,products,{...value,optionSelections:toggleProductOptionSelection(value.optionSelections,set,id)}))}/>}
    {direct?null:<label className="cfg-note cfg19-note"><span>備註</span><input maxLength={60} value={value.freeNote} onChange={event=>onChange(workspaceChoiceConfiguration(choice,products,{...value,freeNote:event.target.value}))}/></label>}
    {!configured.valid?<p role="alert">選項未揀齊</p>:null}
  </section>;
}

export function PublishedInlineOptionSets({sets,selected,onSelect}:{sets:readonly SyncedOptionSet[];selected:Readonly<Record<string,readonly string[]>>;onSelect:(set:SyncedOptionSet,optionId:string)=>void}){
  return <div className="direct-drink-options">{sets.map(set=><fieldset key={set.id} aria-label={set.name}>
    <legend>{set.name.replace(/^套餐普通飲品/,'')}</legend>
    <div>{set.options.map(option=>{
      const on=Boolean(selected[set.id]?.includes(option.id));
      const full=set.selection==='MULTI'&&!on&&(selected[set.id]?.length??0)>=set.max;
      return <button key={option.id} type="button" disabled={!option.active||full} aria-pressed={on} onClick={()=>onSelect(set,option.id)}>{frontlineOptionName(option)}{option.priceAdjustmentMinor?<small>{money(option.priceAdjustmentMinor)}</small>:null}</button>;
    })}</div>
  </fieldset>)}</div>;
}

export function ProductConfigWorkspace({
  product,initial,mode='add',pricingBaseMinor,onAdd,onClose,mealDrinkChoices=[],mealDrinkProducts=[],priceOverride,studentSaving,drinkStudentSaving,showNote=true,directOptions=false,fixedQty=false,basePicker,totalLabel,submitLabel
}:{
  /** Bento family base selector, shown as the first section of the left column. */
  basePicker?:ReactNode;
  product:WorkspaceProduct;
  /** Hide note entry when the caller's existing command does not support free notes. */
  showNote?:boolean;
  /** Show every published option in this modal; used by drink editing to avoid a second popover. */
  directOptions?:boolean;
  /** Meal-bound drinks inherit the number of selected cups; staff changes the target cards, not this quantity. */
  fixedQty?:boolean;
  totalLabel?:string;
  submitLabel?:string;
  onClose?:()=>void;
  initial?:{readonly qty:number;readonly detail?:string;readonly optionSelections?:MfkOrderLineOptionSelectionsV1;readonly freeNote?:string;readonly mealDrinkChoiceId?:string;readonly mealDrinkConfiguration?:WorkspaceChoiceConfiguration;readonly unitOverrideMinor?:number;readonly overrideReason?:string;readonly studentUnits?:number};
  /** #43-c3：跟餐飲品每杯學生優惠（0＝呢款唔適用）；有傳先顯示「學生」掣 */
  studentSaving?:(choiceId:string,configuredUnitMinor?:number)=>number;
  /** #43-c3：呢張卡本身係餐嘅特飲（飲品設定）：按每杯價計學生優惠（0＝唔適用） */
  drinkStudentSaving?:(unitMinor:number)=>number;
  /** 現場改價：有傳先顯示「改價」；allowed=false 時撳落會講原因 */
  priceOverride?:{readonly allowed:boolean};
  mode?:'add'|'edit';
  pricingBaseMinor?:number;
  onAdd:(detail:string,deltaMinor:number,qty:number,structured:{readonly optionSelections:MfkOrderLineOptionSelectionsV1;readonly freeNote:string;readonly mealDrinkChoiceId?:string;readonly mealDrinkConfiguration?:WorkspaceChoiceConfiguration;readonly unitOverrideMinor?:number;readonly overrideReason?:string;readonly studentUnits?:number})=>void;
  mealDrinkChoices?:readonly WorkspaceDrinkSupplementChoice[];
  mealDrinkProducts?:readonly WorkspaceProduct[];
}){
  const initialState=useMemo(()=>productEditorInitialFromDetail(product,initial?.detail),[product,initial?.detail]);
  const priceBase=pricingBaseMinor??product.priceMinor;
  const [qty,setQty]=useState(initial?.qty??1);
  const [overrideMinor,setOverrideMinor]=useState<number|null>(initial?.unitOverrideMinor??null);
  const [overrideReason,setOverrideReason]=useState(initial?.overrideReason??'');
  const [priceEdit,setPriceEdit]=useState<{digits:string;reason:string}|null>(null);
  const [priceDenied,setPriceDenied]=useState(false);
  const [mealDrinkChoiceId,setMealDrinkChoiceId]=useState(initial?.mealDrinkChoiceId??'pending');
  const [studentUnits,setStudentUnits]=useState(initial?.studentUnits??0);
  const chosenDrink=mealDrinkChoices.find(choice=>choice.id===mealDrinkChoiceId);
  const [drinkConfiguration,setDrinkConfiguration]=useState(()=>chosenDrink?workspaceChoiceConfiguration(chosenDrink,mealDrinkProducts,initial?.mealDrinkConfiguration):undefined);
  const configuredDrink=chosenDrink?workspaceChoiceConfiguration(chosenDrink,mealDrinkProducts,drinkConfiguration):undefined;
  const drinkOptionSets=chosenDrink&&chosenDrink.kind!=='NONE'?drinkSupplementConfigurationView(chosenDrink,mealDrinkProducts)?.optionSets??[]:[];
  const drinkHasOptions=drinkOptionSets.length>0;
  const drinkPickedNames=configuredDrink?drinkOptionSets.flatMap(set=>set.options.filter(option=>configuredDrink.optionSelections[set.id]?.includes(option.id)).map(frontlineOptionName)).join('・'):'';
  const changeDrink=(choiceId:string)=>{if(choiceId===mealDrinkChoiceId)return;const choice=mealDrinkChoices.find(row=>row.id===choiceId);setMealDrinkChoiceId(choiceId);setDrinkConfiguration(choice?workspaceChoiceConfiguration(choice,mealDrinkProducts):undefined);};
  const [note,setNote]=useState(initial?.freeNote??initialState.note);
  const [selected,setSelected]=useState<Record<string,string[]>>(()=>Object.fromEntries(
    Object.entries(initial?.optionSelections??initialState.selected).map(([id,ids])=>[id,[...ids]]),
  ));

  const toggle=(set:SyncedOptionSet,optionId:string)=>{
    setSelected(current=>toggleProductOptionSelection(current,set,optionId));
  };
  const selectedOptions=(product.optionSets??[]).flatMap(set=>{
    const ids=new Set(selected[set.id]??[]);
    return set.options.filter(option=>ids.has(option.id));
  });
  const delta=selectedOptions.reduce((sum,option)=>sum+option.priceAdjustmentMinor,0);
  const invalid=Boolean(chosenDrink&&!configuredDrink?.valid)||(product.optionSets??[]).some(set=>{
    const count=(selected[set.id]??[]).length;
    return count<set.min||count>set.max||(set.required&&count<1);
  });
  // 便當合一 P2：揀飯底即刻睇到 Admin 範本拼出嚟嘅名同編號（例如 C12 鹽酥雞咖喱便當）
  const variantSet=(product.optionSets??[]).find(set=>set.variant?.enabled);
  const variantPreview=variantSet&&selected[variantSet.id]?.[0]?resolveProductVariant(product,variantSet,selected[variantSet.id]![0]!):undefined;
  const detail=[
    ...(product.optionSets??[]).flatMap(set=>{
      const ids=new Set(selected[set.id]??[]);
      const names=set.options.filter(option=>ids.has(option.id)).map(option=>option.name);
      return names.length?[set.name+'：'+names.join('、')]:[];
    }),
    note.trim(),
  ].filter(Boolean).join(' · ');

  const sets=product.optionSets??[];
  const sectionId=(id:string)=>'cfg19-'+product.id+'-'+id;
  const changes=sets.flatMap(set=>{
    const ids=new Set(selected[set.id]??[]);
    return set.options.filter(option=>ids.has(option.id)).map(option=>({set,option,label:frontlineOptionName(option),tone:'pick'}));
  });
  const listedUnit=priceBase+delta;
  const unit=overrideMinor??listedUnit;
  const studentPerCup=drinkStudentSaving?drinkStudentSaving(unit):studentSaving&&mealDrinkChoiceId!=='pending'?studentSaving(mealDrinkChoiceId,(chosenDrink?.adjustmentMinor??0)+(configuredDrink?.delta??0)):0;
  const studentCups=studentPerCup>0?Math.min(qty,studentUnits):0;
  const studentTarget=drinkStudentSaving?{label:product.name,priceMinor:unit}:chosenDrink?{label:chosenDrink.label,priceMinor:chosenDrink.adjustmentMinor+(configuredDrink?.delta??0)}:undefined;
  const studentButton=studentPerCup>0&&studentTarget?<button type="button" className={'cfg19-student'+(studentCups>0?' on':'')} aria-pressed={studentCups>0} onClick={()=>setStudentUnits(studentCups>=qty?0:studentCups+1)}>
    <b>{studentCups>0?'✓ 學生'+(qty>1?' '+studentCups+'/'+qty+' 杯':''):'學生'}</b>
    <small>{studentTarget.label} <s>{money(studentTarget.priceMinor)}</s> → {money(studentTarget.priceMinor-studentPerCup)}{qty>1?' · 撳一下加一杯':''}</small>
  </button>:null;
  const openPriceEdit=()=>{
    if(!priceOverride)return;
    if(!priceOverride.allowed){setPriceDenied(true);return;}
    setPriceEdit({digits:String(Math.round(unit/100)),reason:overrideReason});
  };
  const priceDraftMinor=priceEdit?Number(priceEdit.digits||'0')*100:0;
  const pressPriceKey=(key:string)=>setPriceEdit(current=>{
    if(!current)return current;
    if(key==='back')return {...current,digits:current.digits.slice(0,-1)};
    if(key==='clear')return {...current,digits:''};
    const digits=(current.digits==='0'?'':current.digits)+key;
    return digits.length>5?current:{...current,digits};
  });
  const confirmPriceEdit=()=>{
    if(!priceEdit)return;
    if(priceDraftMinor===listedUnit){setOverrideMinor(null);setOverrideReason('');}
    else{setOverrideMinor(priceDraftMinor);setOverrideReason(priceEdit.reason.trim().slice(0,60));}
    setPriceEdit(null);
  };
  const submit=()=>{if(invalid||priceEdit)return;onAdd(detail,delta,qty,{
      ...(overrideMinor!==null&&overrideMinor!==listedUnit?{unitOverrideMinor:overrideMinor,overrideReason}:{}),
      optionSelections:Object.freeze(Object.fromEntries(
        Object.entries(selected).flatMap(([id,ids])=>ids.length?[[id,Object.freeze([...ids])] as const]:[])
      )),
      freeNote:note.trim(),
      ...(mealDrinkChoices.length?{mealDrinkChoiceId,...(configuredDrink?{mealDrinkConfiguration:configuredDrink}:{})}:{}),
      ...(drinkStudentSaving||studentSaving&&mealDrinkChoices.length?{studentUnits:studentCups}:{}),
    });};
  // 改價鍵盤開住：實體鍵盤數字／Backspace／Enter／Esc 都直接用（唔使先撳中個框）
  const priceKeyRef=useRef<(event:KeyboardEvent)=>void>(()=>{});
  priceKeyRef.current=(event:KeyboardEvent)=>{
    if(!priceEdit)return;
    if(event.target instanceof HTMLInputElement&&event.key!=='Enter'&&event.key!=='Escape')return;
    if(/^\d$/.test(event.key)){event.preventDefault();event.stopPropagation();pressPriceKey(event.key);return;}
    if(event.key==='Backspace'){event.preventDefault();event.stopPropagation();pressPriceKey('back');return;}
    if(event.key==='Escape'){event.preventDefault();event.stopPropagation();setPriceEdit(null);return;}
    if(event.key==='Enter'){event.preventDefault();event.stopPropagation();confirmPriceEdit();}
  };
  const priceOpen=Boolean(priceEdit);
  useEffect(()=>{
    if(!priceOpen)return;
    const listener=(event:KeyboardEvent)=>priceKeyRef.current(event);
    window.addEventListener('keydown',listener,true);
    return()=>window.removeEventListener('keydown',listener,true);
  },[priceOpen]);
  const onKeyDown=(event:{key:string;target:EventTarget;preventDefault:()=>void;stopPropagation?:()=>void})=>{
    if(priceEdit)return;
    if(event.key==='Enter'&&!(event.target instanceof HTMLInputElement)&&!(event.target instanceof HTMLButtonElement)){event.preventDefault();submit();}
  };

  return <div className="cfg-workspace cfg19" onKeyDown={onKeyDown} tabIndex={-1}>
    <header className="cfg19-head">
      <div><h2 className={variantPreview?'cfg-variant-name':undefined} key={variantPreview?.receiptName??product.name}>{variantPreview?.receiptName??product.name}</h2><span className="modal28-meta">{qty} 件</span></div>
    </header>
    <div className="cfg19-body">
      <section className="cfg19-left">
        {basePicker}
        <section className="cfg19-card cfg19-portion-card">{sets.length&&!directOptions?<header><b>選項</b></header>:null}{directOptions?<PublishedInlineOptionSets sets={sets} selected={selected} onSelect={toggle}/>:<PublishedOptionTiles sets={sets} selected={selected} onSelect={toggle} productCode={product.productCode}/>}</section>
        {mealDrinkChoices.length?<section className="cfg-block cfg19-card" id={sectionId('meal')}><header><b>飲品</b></header><div className={`cfg19-drink-body${drinkHasOptions?' has-choice':''}`}><div className="cfg19-chips cfg19-drink-choices"><button type="button" className={mealDrinkChoiceId==='pending'?'on':''} onClick={()=>changeDrink('pending')}><b>稍後決定</b></button>{mealDrinkChoices.map(choice=><button type="button" key={choice.id} disabled={!choice.enabled} className={mealDrinkChoiceId===choice.id?'on':''} onClick={()=>changeDrink(choice.id)}><b>{choice.label}</b>{choice.adjustmentMinor?<small>{mfMoney(choice.adjustmentMinor,{sign:true})}</small>:null}</button>)}</div>
          {drinkHasOptions&&chosenDrink&&configuredDrink?<PublishedChoiceFields direct choice={chosenDrink} products={mealDrinkProducts} value={configuredDrink} onChange={value=>setDrinkConfiguration({...value,valid:true})}/>:null}</div>
          {studentButton}</section>:null}
        {showNote?<label className="cfg-note cfg19-card cfg19-note" id={sectionId('note')}><span>備註</span><input value={note} maxLength={60} onChange={event=>setNote(event.target.value)} placeholder="例如：唔要蔥、醬分開"/><small>{note.length}/60</small></label>:null}
      </section>
      <aside className="cfg19-side">
        {fixedQty?<div className="cfg19-qty cfg19-qty-fixed" aria-label={`已選 ${qty} 杯`}><span>已選</span><b key={qty}>{qty}</b><span>杯</span></div>:<div className="cfg19-qty"><button type="button" aria-label="減少數量" disabled={qty<=1} onClick={()=>setQty(Math.max(1,qty-1))}>−</button><b key={qty}>{qty}</b><button type="button" aria-label="增加數量" onClick={()=>setQty(qty+1)}>＋</button></div>}
        <div className="cfg19-sum">
          <b>已揀</b>
          <ul className="cfg19-picked">
            {changes.map(({set,option,label})=><li key={set.id+option.id}><span>{label}</span>{option.priceAdjustmentMinor?<em>{mfMoney(option.priceAdjustmentMinor,{sign:true})}</em>:null}</li>)}
            {mealDrinkChoices.length?(chosenDrink?<li><span>{chosenDrink.label}{drinkPickedNames?<small>{drinkPickedNames}</small>:null}</span>{chosenDrink.adjustmentMinor+(configuredDrink?.delta??0)?<em>{mfMoney(chosenDrink.adjustmentMinor+(configuredDrink?.delta??0),{sign:true})}</em>:null}</li>:<li className="pending"><span>飲品稍後決定</span></li>):null}
            {!changes.length&&!mealDrinkChoices.length?<li className="pending"><span>無加減</span></li>:null}
          </ul>
          {showNote&&note.trim()?<p className="note">備註：{note.trim()}</p>:null}
          {studentCups>0?<p className="cfg19-student-line">學生優惠 {studentCups} 杯 −{money(studentPerCup*studentCups)}（結帳時計）</p>:null}
          {qty>1||delta!==0?<p className="calc">每份 {money(priceBase)}{delta?(delta>0?' + ':' − ')+money(Math.abs(delta))+' = '+money(listedUnit):''}{qty>1&&overrideMinor===null?' × '+qty:''}</p>:null}
          {overrideMinor!==null?<p className="cfg19-override-line"><span>現場改價</span><s>{money(listedUnit)}</s><b>{money(overrideMinor)}</b>{qty>1?<em>× {qty}</em>:null}{overrideReason?<small>{overrideReason}</small>:null}</p>:null}
        </div>
        {priceOverride?<div className="cfg19-price-actions">
          <button type="button" className="cfg19-price-edit" onClick={openPriceEdit}>{overrideMinor!==null?'再改價':'改價'}</button>
          {overrideMinor!==null?<button type="button" className="cfg19-price-reset" onClick={()=>{setOverrideMinor(null);setOverrideReason('');}}>還原 {money(listedUnit)}</button>:null}
        </div>:null}
        {priceDenied?<p className="cfg19-price-denied" role="status">未有改價權限，請聯絡主管。<button type="button" onClick={()=>setPriceDenied(false)}>知道</button></p>:null}
        {drinkStudentSaving?studentButton:null}
        <div className={'cfg-action cfg19-total'+(overrideMinor!==null?' overridden':'')}><span>{totalLabel??'小計'}</span><b key={unit*qty}>{mealDrinkChoices.length?money((unit+(chosenDrink?.adjustmentMinor??0)+(configuredDrink?.delta??0))*qty):money(unit*qty)}</b></div>
        <div className="cfg19-result-actions">{onClose?<button type="button" className="cfg19-return" onClick={onClose}>返回</button>:null}<button type="button" className="primary cfg19-add" disabled={invalid} onClick={submit}>{submitLabel??(mode==='edit'?'儲存修改':'加入')}・{money(mealDrinkChoices.length?(unit+(chosenDrink?.adjustmentMinor??0)+(configuredDrink?.delta??0))*qty:unit*qty)}<kbd>Enter</kbd></button></div>
        {invalid?<small className="cfg19-warn">仲有必選未揀</small>:null}
      </aside>
    </div>
    {priceEdit?<div className="cfg19-price-layer" role="dialog" aria-modal="true" aria-label="改價" onMouseDown={event=>{if(event.target===event.currentTarget)setPriceEdit(null);}}>
      <div className="cfg19-price-sheet">
        <header><b>改價 · 每份</b><small>原價 {money(listedUnit)}</small></header>
        <div className={'cfg19-price-display'+(priceDraftMinor!==listedUnit?' changed':'')}><span>成交價</span><b>{money(priceDraftMinor)}</b></div>
        {priceDraftMinor!==listedUnit?<p className="cfg19-price-diff">{priceDraftMinor<listedUnit?'平咗 '+money(listedUnit-priceDraftMinor):'貴咗 '+money(priceDraftMinor-listedUnit)} · 每份</p>:<p className="cfg19-price-diff same">同原價一樣</p>}
        <div className="cfg19-price-pad">{['1','2','3','4','5','6','7','8','9','clear','0','back'].map(key=><button type="button" key={key} className={key.length>1?'fn':''} onClick={()=>pressPriceKey(key)} aria-label={key==='back'?'刪除一位':key==='clear'?'清除':undefined}>{key==='back'?'←':key==='clear'?'清除':key}</button>)}</div>
        <label className="cfg19-price-reason"><span>原因（可選）</span><input value={priceEdit.reason} maxLength={60} placeholder="例如：熟客、補償" onChange={event=>{const reason=event.target.value;setPriceEdit(current=>current?{...current,reason}:current);}}/></label>
        <footer><button type="button" onClick={()=>setPriceEdit(null)}>取消<kbd>Esc</kbd></button><button type="button" className="primary" onClick={confirmPriceEdit}>確定 {money(priceDraftMinor)}<kbd>Enter</kbd></button></footer>
      </div>
    </div>:null}
  </div>;
}

export function DrinkSupplementWorkspace({
  cart,choices,onAdd,onConfigure,
}:{
  cart:readonly WorkspaceCartLine[];
  choices:readonly WorkspaceDrinkSupplementChoice[];
  onAdd:(choiceId:string,qty:number,targetLineId?:string)=>void;
  onConfigure:(choiceId:string,qty:number,targetLineId?:string)=>void;
}){
  const targets=cart.filter(line=>!isDrinkSupplementProductId(line.productId));
  const [qty,setQty]=useState(1);
  const [targetLineId,setTargetLineId]=useState('');
  return <section className="drink-supplement-workspace" aria-label="飲品補選">
    <header>
      <div><h3>飲品補選</h3></div>

    </header>
    <div className="drink-supplement-controls">
      <div className="drink-targets">
        <b>配餐</b>
        <button type="button" className={targetLineId===''?'active':''} onClick={()=>setTargetLineId('')}>未指定</button>
        {targets.map((line,index)=><button type="button" key={line.id} className={targetLineId===line.id?'active':''} onClick={()=>setTargetLineId(line.id)}>
          {index+1}　{line.name}
        </button>)}
      </div>
      <div className="drink-qty"><span>數量</span><button type="button" onClick={()=>setQty(Math.max(1,qty-1))}>−</button><b>{qty}</b><button type="button" onClick={()=>setQty(qty+1)}>＋</button></div>
    </div>
    <div className="drink-supplement-grid">
      {choices.length?choices.map(choice=><button
        type="button"
        key={choice.id}
        disabled={!choice.enabled}
        onClick={()=>choice.requiresConfiguration
          ?onConfigure(choice.id,qty,targetLineId||undefined)
          :onAdd(choice.id,qty,targetLineId||undefined)}
      >
        <b>{choice.label}</b>
        <small>{choice.adjustmentMinor===0?'餐內':(choice.adjustmentMinor>0?'+':'')+money(choice.adjustmentMinor)}</small>
        {choice.requiresConfiguration?<em>有選項</em>:null}
      </button>):<p>未有飲品</p>}
    </div>
  </section>;
}

export function RequiredFastLaneWorkspace({
  cart,products,onApply,drinkChoices=[],onAddDrink,onConfigureDrink,
}:{
  cart:readonly WorkspaceCartLine[];
  products:readonly WorkspaceProduct[];
  onApply:(lineId:string,groupId:string,optionIds:readonly string[])=>void;
  drinkChoices?:readonly WorkspaceDrinkSupplementChoice[];
  onAddDrink?:(choiceId:string,qty:number,targetLineId?:string)=>void;
  onConfigureDrink?:(choiceId:string,qty:number,targetLineId?:string)=>void;
}){
  const tasks=useMemo(()=>requiredTasksForCart(cart,products),[cart,products]);
  const [draft,setDraft]=useState<Record<string,string[]>>({});
  const toggle=(task:WorkspaceRequiredTask,optionId:string)=>{
    const current=draft[task.id]??[...task.selectedOptionIds];
    if(task.selection==='SINGLE'){
      setDraft(value=>({...value,[task.id]:[optionId]}));
      return;
    }
    const next=current.includes(optionId)?current.filter(id=>id!==optionId):[...current,optionId];
    setDraft(value=>({...value,[task.id]:next.slice(0,task.max)}));
  };

  return <div className="required-fast-lane">
    <header className="required-fast-title">
      <div><h2>必選／補選</h2></div>
      <strong>{tasks.length} 項</strong>
    </header>
    {tasks.length?<div className="required-fast-list">{tasks.map((task,index)=>{
      const chosen=draft[task.id]??task.selectedOptionIds;
      const ready=chosen.length>=task.min&&chosen.length<=task.max;
      return <article key={task.id} className={index===0?'current':''}>
        <header><span>{index+1}</span><div><b>{task.lineName}</b><small>{task.groupName} · 最少 {task.min} / 最多 {task.max}</small></div><em>{task.missingCount>0?'欠 '+task.missingCount:'需修正'}</em></header>
        <div className="required-fast-options">{task.options.map(option=>{
          const active=chosen.includes(option.id);
          return <button type="button" key={option.id} className={active?'active':''} onClick={()=>toggle(task,option.id)}>
            <b>{frontlineOptionName(option)}</b>{option.priceAdjustmentMinor!==0?<small>{option.priceAdjustmentMinor>0?'+':''}{money(option.priceAdjustmentMinor)}</small>:null}
          </button>;
        })}</div>
        <footer><span>{task.selection==='SINGLE'?'單選':'多選'} · 已選 {chosen.length}</span><button type="button" disabled={!ready} onClick={()=>onApply(task.lineId,task.groupId,chosen)}>套用選項</button></footer>
      </article>;
    })}</div>:<div className="required-fast-empty"><b>必選已齊</b></div>}
    {onAddDrink&&onConfigureDrink?<DrinkSupplementWorkspace cart={cart.filter(line=>!line.meal&&!line.pairing)} choices={drinkChoices} onAdd={onAddDrink} onConfigure={onConfigureDrink}/>:null}
  </div>;
}

export function OrganizeWorkspace({lines,products=[],onApplyVariant,onDone}:{lines:readonly WorkspaceCartLine[];products?:readonly WorkspaceProduct[];onApplyVariant?:(lineId:string,groupId:string,optionIds:readonly string[])=>void;onDone:()=>void}){
  const mealLines=lines.filter(line=>!isDrinkSupplementProductId(line.productId));
  // 便當合一 P2：飯底按鈕讀 Admin 變體組（唔再寫死肉燥／咖喱／菜飯），撳咗即刻改購物車
  const byProduct=new Map(products.map(product=>[product.id,product] as const));
  const variantSetOf=(line:WorkspaceCartLine)=>byProduct.get(line.productId)?.optionSets?.find(set=>set.variant?.enabled);
  const unresolved=mealLines.filter(line=>{const set=variantSetOf(line);return set&&!(line.optionSelections?.[set.id]?.length);}).length;
  return <div className="organize-workspace">
    <header className="organize-title"><h2>整理工作台</h2><div><span>未完成 {unresolved}</span><span>可配對 {Math.ceil(mealLines.length/2)}</span></div></header>
    <section className="organize-section"><header><b><i>1</i> 必選</b><span>{mealLines.length}</span></header>
      <div className="organize-required">{mealLines.map((line,index)=>{
        const set=variantSetOf(line);
        const current=set?line.optionSelections?.[set.id]?.[0]:undefined;
        return <article key={line.id}><div><b>{index+1}　{line.name}</b><small>{line.detail??(set?set.name:'冇必選項')}</small></div>{set?<div className="organize-options">{set.options.map(option=><button key={option.id} type="button" className={current===option.id?'active':''} aria-pressed={current===option.id} onClick={()=>onApplyVariant?.(line.id,set.id,[option.id])}>{frontlineOptionName(option)}</button>)}</div>:null}</article>;
      })}</div>
    </section>
    <section className="organize-section"><header><b><i>2</i> 配對／代補</b><span>{Math.ceil(mealLines.length/2)}</span></header>
      <div className="organize-pairs">{Array.from({length:Math.ceil(mealLines.length/2)},(_,idx)=>{const a=mealLines[idx*2],b=mealLines[idx*2+1];return <article key={idx}><strong>{idx+1} 組</strong><div>{a?<span>{a.name}</span>:null}{b?<span>{b.name}</span>:<em>＋ 未配對</em>}</div></article>})}</div>
    </section>
    <section className="organize-section"><header><b><i>3</i> 飲品補選</b><span>可跳過</span></header></section>
    <footer className="organize-footer"><button onClick={onDone}>完成整理</button></footer>
  </div>;
}

export function ComboWorkspace({products,combos,pools,onAdd,onClose,initialComboId,initialSelections=[],initialQuantity=1}:{
  products:readonly WorkspaceProduct[];combos:readonly SyncedCombo[];pools:readonly SyncedComboPool[];
  onAdd:(comboId:string,comboName:string,detail:string,unitMinor:number,selections:readonly WorkspaceComboSelection[],quantity?:number)=>void;
  onClose?:()=>void;initialComboId?:string;initialSelections?:readonly WorkspaceComboSelection[];initialQuantity?:number;
}){
  const activeCombos=combos.filter(combo=>combo.active);
  const [comboId,setComboId]=useState(initialComboId??activeCombos[0]?.id??'');
  const [quantity,setQuantity]=useState(initialQuantity);
  const [step,setStep]=useState(0);
  const [selected,setSelected]=useState<Record<string,string>>(()=>Object.fromEntries(initialSelections.map(ref=>[JSON.stringify([ref.poolId,ref.groupId]),[ref.poolId,ref.groupId,ref.subPoolId,ref.choiceId].join('::')])));
  const [configurations,setConfigurations]=useState<Record<string,WorkspaceChoiceConfiguration>>(()=>Object.fromEntries(initialSelections.flatMap(ref=>ref.configuration?[[JSON.stringify([ref.poolId,ref.groupId]),ref.configuration]]:[])));
  const combo=activeCombos.find(row=>row.id===comboId)??activeCombos[0];
  const poolById=useMemo(()=>new Map(pools.map(pool=>[pool.id,pool] as const)),[pools]);
  const productById=useMemo(()=>new Map(products.map(product=>[product.id,product] as const)),[products]);
  const selectedPools=combo?[combo.mainPoolId,...combo.addonPoolIds].filter(Boolean).map(id=>poolById.get(id!)).filter((pool):pool is SyncedComboPool=>Boolean(pool)):[];
  const groups=selectedPools.flatMap(pool=>pool.groups.map(group=>({pool,group})));
  const groupLabel=(pool:SyncedComboPool,group:SyncedComboPool['groups'][number])=>pool.addonKind==='DRINK'?'飲品':pool.addonKind==='SNACK'?'小食':group.name;
  const keyFor=(poolId:string,groupId:string)=>JSON.stringify([poolId,groupId]);
  const viewFor=(pool:SyncedComboPool,group:SyncedComboPool['groups'][number],subPool:SyncedComboPool['groups'][number]['subPools'][number],choice:SyncedComboPool['groups'][number]['subPools'][number]['choices'][number]):WorkspaceDrinkSupplementChoice=>({
    id:[pool.id,group.id,subPool.id,choice.id].join('::'),label:choice.type==='PRODUCT'?productById.get(choice.productId??'')?.name??choice.label:choice.label,kind:choice.type,productId:choice.productId,
    enabled:subPool.active&&choice.active&&(choice.type!=='PRODUCT'||productById.has(choice.productId??'')),adjustmentMinor:subPool.priceAdjustmentMinor+choice.priceAdjustmentMinor,
    requiresConfiguration:Boolean(choice.type==='PRODUCT'?productById.get(choice.productId??'')?.optionSets?.length:choice.optionSets?.length),optionSets:choice.type==='LABEL'?choice.optionSets:undefined,
  });
  const resolveChoice=(poolId:string,groupId:string)=>{
    const key=keyFor(poolId,groupId),resolved=resolveWorkspaceComboChoice(pools,poolId,groupId,selected[key]);
    if(!resolved)return null;
    const {pool,group,subPool,choice}=resolved,view=viewFor(pool,group,subPool,choice);
    return {subPool,choice,view,configuration:workspaceChoiceConfiguration(view,products,configurations[key])};
  };
  const requiredMissing=groups.some(({pool,group})=>pool.addonKind!=='DRINK'&&(group.required||group.min>0)&&!resolveChoice(pool.id,group.id));
  const invalid=requiredMissing||groups.some(({pool,group})=>{const resolved=resolveChoice(pool.id,group.id);return group.min>1||group.max<1&&Boolean(resolved)||Boolean(resolved&&!resolved.configuration.valid);});
  const selections=groups.flatMap(({pool,group})=>{const resolved=resolveChoice(pool.id,group.id);return resolved?[{poolId:pool.id,groupId:group.id,subPoolId:resolved.subPool.id,choiceId:resolved.choice.id,configuration:resolved.configuration}]:[];});
  const additions=groups.reduce((sum,{pool,group})=>{const resolved=resolveChoice(pool.id,group.id);return sum+(resolved?.view.adjustmentMinor??0)+(resolved?.configuration.delta??0);},0);
  const total=(combo?.basePriceMinor??0)+additions;
  const detail=groups.flatMap(({pool,group})=>{const resolved=resolveChoice(pool.id,group.id);return resolved?[group.name+'：'+resolved.view.label+(resolved.configuration.detail?' · '+resolved.configuration.detail:'')]:[];}).join(' · ');
  if(!combo)return <div className="combo-workspace"><div className="ordering-empty">未有套餐</div></div>;
  const choiceSummary=(view:WorkspaceDrinkSupplementChoice,configuration:WorkspaceChoiceConfiguration)=>[...(drinkSupplementConfigurationView(view,products)?.optionSets??[]).flatMap(set=>set.options.filter(option=>configuration.optionSelections[set.id]?.includes(option.id)).map(frontlineOptionName)),configuration.freeNote].filter(Boolean).join('・');
  const currentStep=Math.min(step,Math.max(0,groups.length-1));
  const activeGroup=groups[currentStep];
  return <div className="combo-workspace combo26 custom-combo">
    <header className="combo-title"><h2>套餐</h2><label className="modal28-meta">份數 <input aria-label="套餐份數" type="number" min={1} max={99} value={quantity} onChange={event=>setQuantity(Math.max(1,Math.floor(Number(event.target.value)||1)))}/></label><strong>{money(total)}</strong></header>
    <div className="combo-tiers">{activeCombos.map(row=><button type="button" key={row.id} aria-pressed={combo.id===row.id} className={combo.id===row.id?'active':''} onClick={()=>{setComboId(row.id);setSelected({});setConfigurations({});setStep(0);}}><b>{row.name}</b><span>{money(row.basePriceMinor)}</span></button>)}</div>
    <div className="combo26-body">

      <div className="combo26-current">{activeGroup?(()=>{const {pool,group}=activeGroup,key=keyFor(pool.id,group.id),resolved=resolveChoice(pool.id,group.id);return <section className="combo-section" key={key}>
        <header><h3>{groupLabel(pool,group)}</h3><span>{group.required?'必選':'可選'} {group.min+'–'+group.max}</span></header>
        <div className={pool.addonKind==='DRINK'?'combo26-drink-body':''}><div>{group.subPools.map(subPool=><div key={subPool.id} className="combo-admin-subpool"><header>{subPool.name!==group.name?<strong>{subPool.name}</strong>:null}{subPool.priceAdjustmentMinor!==0&&!subPool.name.includes(money(subPool.priceAdjustmentMinor).replace(/\.00$/,''))?<span>{subPool.priceAdjustmentMinor>0?'+':''}{money(subPool.priceAdjustmentMinor)}</span>:null}</header>
          <div className="combo-product-grid">{subPool.choices.map(choice=>{const view=viewFor(pool,group,subPool,choice),on=selected[key]===view.id;return <button type="button" key={choice.id} aria-pressed={on} disabled={!view.enabled} className={on?'active':''} onClick={()=>{if(on){if(!group.required&&group.min===0){setSelected(current=>{const next={...current};delete next[key];return next;});setConfigurations(current=>{const next={...current};delete next[key];return next;});}return;}setSelected(current=>({...current,[key]:view.id}));setConfigurations(current=>({...current,[key]:workspaceChoiceConfiguration(view,products)}));}}><b>{view.label}</b>{choice.priceAdjustmentMinor!==0?<small>{choice.priceAdjustmentMinor>0?'+':''}{money(choice.priceAdjustmentMinor)}</small>:null}</button>;})}</div>
        </div>)}</div>
        {resolved&&resolved.view.kind!=='NONE'?<PublishedChoiceFields direct choice={resolved.view} products={products} value={resolved.configuration} onChange={value=>setConfigurations(current=>({...current,[key]:value}))}/>:null}</div>
      </section>;})():null}</div>
    </div>
    <footer className="combo-footer custom-combo-footer">{onClose?<button type="button" className="combo26-return" onClick={onClose}>返回</button>:null}{groups.map(({pool,group},index)=><button type="button" key={keyFor(pool.id,group.id)} aria-pressed={currentStep===index} onClick={()=>setStep(index)}><b>{group.name}</b><small>{resolveChoice(pool.id,group.id)?.view.label??(pool.addonKind==='DRINK'?'稍後揀':'未選')}</small></button>)}<button disabled={invalid} onClick={()=>onAdd(combo.id,combo.name,detail,total,selections,quantity)}>加入・{money(total*quantity)}</button></footer>
  </div>;
}

export interface HoldPlacementTable{
  readonly id:string;
  readonly label:string;
  readonly occupied:boolean;
  readonly codeLabel?:string;
  readonly partySize?:number;
  readonly totalMinor?:number;
}

export function initialHoldModeForLines(lines:readonly {readonly serviceMode:ServiceMode}[]):'cart'|'dining'{
  return lines.some(line=>line.serviceMode==='dine-in')?'dining':'cart';
}

export function HoldCartWorkspace({
  lines,totalMinor,tables,initialMode='cart',onHoldWaiting,onHoldQueue,onHoldTable,
  receiptLines,packagingLabel='$0',quickNotes=[],onClose,busy=false,error,onRetry
}:{
  lines:readonly WorkspaceCartLine[];
  totalMinor:number;
  tables:readonly HoldPlacementTable[];
  initialMode?:'cart'|'dining';
  onHoldWaiting:(partySize:number,note:string)=>void;
  onHoldQueue:(partySize:number,note:string)=>void;
  onHoldTable:(tableId:string,partySize:number,note:string)=>void;
  receiptLines?:readonly CartLineViewModel[];
  packagingLabel?:string;
  quickNotes?:readonly {id:string;label:string}[];
  onClose?:()=>void;
  busy?:boolean;
  error?:string;
  onRetry?:()=>void;
}){
  const [mode,setMode]=useState<'cart'|'dining'>(initialMode);
  const [partySize,setPartySize]=useState(2);
  const [note,setNote]=useState('');
  const [customerName,setCustomerName]=useState('');
  const [chosenNotes,setChosenNotes]=useState<readonly string[]>([]);
  const [tableId,setTableId]=useState<string|null>(null);
  const table=tables.find(row=>row.id===tableId);
  const joinedNote=[customerName.trim(),...quickNotes.filter(row=>chosenNotes.includes(row.id)).map(row=>row.label),note.trim()].filter(Boolean).join(' · ');
  const receipt=receiptLines??lines.map(line=>({id:line.id,name:line.name,quantity:line.qty,lineTotalLabel:money(line.qty*line.unitMinor),detail:line.detail}));
  return <div className="hold-cart-workspace hold28" aria-busy={busy}>
    <section className="hold28-tabs" role="tablist" aria-label="暫存或堂食">
      <button type="button" role="tab" aria-selected={mode==='cart'} onClick={()=>setMode('cart')} disabled={busy}><HoldWorkspaceGlyph/><b>暫存</b><span>呢張單</span></button>
      <button type="button" role="tab" aria-selected={mode==='dining'} onClick={()=>setMode('dining')} disabled={busy}><HoldWorkspaceGlyph table/><span>放入</span><b>堂食</b></button>
    </section>
    <div className={'hold28-body '+(mode==='cart'?'hold28-save':'hold28-dining')}>
      {mode==='cart'?<section className="hold28-receipt" aria-label="暫存訂單內容">
        <header><b>呢張單</b><span>{receipt.reduce((sum,line)=>sum+line.quantity,0)} 件</span></header>
        <div className="hold28-receipt-lines">{receipt.map(line=><article key={line.id}>
          <div className="hold28-receipt-row"><b>{line.name}{line.quantity>1?' ×'+line.quantity:''}</b><i aria-hidden="true"/><strong>{line.lineTotalLabel}</strong></div>
          {line.detail?<small>{line.detail}</small>:null}
          {('mealComponents' in line?line.mealComponents:undefined)?.map(child=><small key={child.id}>↳ {child.name}{child.detail?' · '+child.detail:''}</small>)}
          {('mealDrinks' in line?line.mealDrinks:undefined)?.map(child=><small key={child.id}>＋ {child.name}{child.detail?' · '+child.detail:''}</small>)}
        </article>)}</div>
        <footer><div><span>包裝費</span><strong>{packagingLabel}</strong></div><div><b>合計</b><strong>{money(totalMinor)}</strong></div></footer>
      </section>:<section className="hold28-tables" aria-label="選擇堂食枱號">
        <header><b>揀枱號</b><label className="hold28-party"><span>人數</span><button type="button" aria-label="減少人數" disabled={busy||partySize<=1} onClick={()=>setPartySize(value=>Math.max(1,value-1))}>−</button><strong>{partySize}</strong><button type="button" aria-label="增加人數" disabled={busy} onClick={()=>setPartySize(value=>value+1)}>＋</button></label></header>
        <button type="button" className="hold28-queue" aria-pressed={tableId===null} onClick={()=>setTableId(null)} disabled={busy}>未有枱・輪候{tableId===null?<span aria-hidden="true">✓</span>:null}</button>
        <div className="hold28-table-grid">{tables.map(row=><button type="button" key={row.id} aria-pressed={row.id===tableId} disabled={busy} className={row.occupied?'occupied':'available'} onClick={()=>setTableId(row.id)}>
          <span className="hold28-table-state"><i aria-hidden="true"/>{row.occupied?'用緊':'空枱'}</span><b>{row.label}</b>
          <small>{row.occupied?(row.partySize??1)+' 人・'+money(row.totalMinor??0)+'・可加單':'可入座'}</small>
          {row.id===tableId?<span className="hold28-table-check" aria-hidden="true">✓</span>:null}
        </button>)}</div>
      </section>}
      <aside className="hold28-notes"><label><span>客人稱呼</span><input aria-label="客人稱呼" maxLength={80} value={customerName} disabled={busy} onChange={event=>setCustomerName(event.target.value)}/></label>
        {quickNotes.length?<fieldset><legend>常用備註</legend><div>{quickNotes.map(row=><button type="button" key={row.id} aria-pressed={chosenNotes.includes(row.id)} disabled={busy} onClick={()=>setChosenNotes(values=>values.includes(row.id)?values.filter(id=>id!==row.id):[...values,row.id])}>{row.label}</button>)}</div></fieldset>:null}
        <label><span>備註</span><textarea aria-label="暫存備註" maxLength={300} rows={3} value={note} disabled={busy} onChange={event=>setNote(event.target.value)}/></label>
      </aside>
    </div>
    {error?<div className="hold28-error" role="alert">{error}{onRetry?<button type="button" disabled={busy} onClick={onRetry}>重試原選擇</button>:null}</div>:null}
    <footer className="hold28-actions"><button type="button" onClick={onClose}>返回</button><span>{mode==='cart'?receipt.reduce((sum,line)=>sum+line.quantity,0)+' 件・'+money(totalMinor):partySize+' 人・'+(table?.label??'輪候')}</span>
      {mode==='cart'?<button type="button" className="primary hold-confirm-waiting" disabled={busy||!lines.length} onClick={()=>onHoldWaiting(partySize,joinedNote)}>{busy?'保存中…':'暫存呢張單'}</button>:<button type="button" className="primary" disabled={busy||!lines.length||Boolean(tableId&&!table)} onClick={()=>table?onHoldTable(table.id,partySize,joinedNote):onHoldQueue(partySize,joinedNote)}>{busy?'保存中…':table?(table.occupied?'加單到'+(table.codeLabel??table.label):'入 '+table.label):'加入輪候'}</button>}
    </footer>
  </div>;
}

function HoldWorkspaceGlyph({table=false}:{table?:boolean}){
  return <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">{table?<><rect x="3" y="6" width="18" height="10" rx="2"/><path d="M6 16v4M18 16v4M7 3v3M17 3v3"/></>:<><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>}</svg>;
}


export interface WorkspaceHoldDraft{
  readonly id:string;
  readonly codeLabel:string;
  readonly kind:'dining'|'waiting';
  readonly createdAt:string;
  readonly partySize:number;
  readonly note:string;
  readonly totalMinor:number;
  readonly assignedTable?:string;
  readonly items:readonly {id:string;name:string;qty:number;unitMinor:number}[];
}

export function HoldListWorkspace({holds,onRestore,onRemove,onClose,tableLabels={}}:{holds:readonly WorkspaceHoldDraft[];onRestore:(hold:WorkspaceHoldDraft)=>void;onRemove:(id:string)=>void;onClose?:()=>void;tableLabels?:Readonly<Record<string,string>>}){
  return <div className="hold-list-workspace hold28-list">
    <div className="hold28-list-groups">{(['waiting','dining'] as const).map(kind=><section key={kind}><header><h3>{kind==='waiting'?'暫存單':'堂食中'}</h3><span>{holds.filter(hold=>hold.kind===kind).length} 張</span></header>
      <div className="hold28-retrieve-grid">{holds.filter(hold=>hold.kind===kind).map(hold=><article key={hold.id}>
        <button type="button" className="hold28-retrieve" onClick={()=>onRestore(hold)} aria-label={'取回 '+hold.codeLabel}>
          <span className={'hold28-retrieve-mark '+kind}>{kind==='waiting'?<HoldWorkspaceGlyph/>:hold.assignedTable?(tableLabels[hold.assignedTable]??hold.assignedTable):'輪候'}</span>
          <span className="hold28-retrieve-copy"><b>{hold.note||hold.codeLabel}</b><small>{hold.codeLabel}・{hold.items.reduce((sum,item)=>sum+item.qty,0)} 件・{hold.items.slice(0,2).map(item=>item.name).join('、')}</small></span>
          <span className="hold28-retrieve-price"><strong>{money(hold.totalMinor)}</strong><small>{holdElapsedLabel(hold.createdAt)}</small></span>
        </button>
        {kind==='waiting'?<button type="button" className="hold28-remove" aria-label={'刪除暫存 '+hold.codeLabel} onClick={()=>onRemove(hold.id)}>×</button>:null}
      </article>)}</div>
      {!holds.some(hold=>hold.kind===kind)?<p className="hold28-empty">{kind==='waiting'?'未有暫存單':'未有堂食單'}</p>:null}
    </section>)}</div>
    <footer className="hold28-actions"><button type="button" onClick={onClose}>返回</button><span>共 {holds.length} 張</span></footer>
  </div>;
}

export function holdElapsedLabel(createdAt:string,now=Date.now()){
  const at=Date.parse(createdAt);
  if(!Number.isFinite(at))return '';
  const minutes=Math.max(0,Math.floor((now-at)/60000));
  return minutes<1?'剛剛':minutes<60?minutes+' 分鐘前':Math.floor(minutes/60)+' 小時前';
}
